import React, { ChangeEvent, useEffect, useState } from "react"
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTransition, animated, useSpring } from '@react-spring/web';
import { Dropdown } from "react-bootstrap";
import moment from "moment";

import {
  createCluster, deleteAssets, deleteCluster, fetchAssets, fetchAssetsForDownload,
  fetchCluster, fetchClusters, fetchAssetsForContentScoreCard, fetchContentScore, invalidateContentData, invalidateContentMapAssets, invalidateDownloadAssets, updateAssets, updateCluster,
  fetchContentGraphValues
} from "src/redux/actions/contentMap";
import { fetchCustomFields } from "src/redux/actions/customFields";

import { Asset } from "src/types/assets";
import { AssetsActionEnum, Cluster, Filter, Operators, PropertyEnum, UpdateAssetsRequest, AssetAccess, CustomFieldItem } from "src/types/contentMap";

import DatePickerComponent from "src/components/datePicker";
import { ClusterView } from "./components/cluster";
import Toast from "../../components/toast";
import { ScrollableContainer } from "src/components/scroller";
import { AutoGrowInput } from "src/components/input";
import Slider from "src/components/slider";
import Button from "src/components/button";
import Thumbnail from "src/components/thumbnail";
import { EditCustomField } from "../asset/components/edit";
import { TagsInput } from "src/components/tagsInput";
import AssetList from "./components/list";
import DownloadExcel from "src/components/downloadExcel";
import ContentScore from "./contentScore";

import { MASTER_ASSET_SCHEMA, UPDATE_CONTENT_EXPIRY_FILTERS } from "src/constants/contentMap";
import { FileService } from "src/services/file.service";

import _ from 'lodash';
import cx from 'classnames';
import styles from './styles.module.scss'
import WarningMessage from "src/components/warningMessage";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { CONTENT_MAP } from "src/constants/adoption/sourceTypes";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import Text from "src/components/text";

import { getSectionType, ScoreCard } from "./contentScore/contentScore.helper";
const defaultAssetImg = '/images/defaultIcons/default_asset.jpg'

interface IReduxState {
  loading: boolean,
  succeeded: boolean,
  items?: any,
  item?: any,
  error: any,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
}

export const useStoreState = (path: string, isRecords: boolean): IReduxState => {

  const reduxData = useSelector((state: any) => {
    const entityTypeData = _.get(state, `${path}`, {});
    const recordType = isRecords ? "items" : "item";

    return {
      loading: _.get(entityTypeData, "loading", false),
      succeeded: _.get(entityTypeData, "succeeded", false),
      [recordType]: _.get(entityTypeData, `${recordType}`, {}),
      error: _.get(entityTypeData, "error", null),
      pagination: _.get(entityTypeData, "pagination", {})
    }

  }, _.isEqual);

  return reduxData;
}

const ContentMap: React.FC<object> = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { 
    trackContentMapScreenView, 
    trackFilterClusterCreated, 
    trackFilterClusterSelected, 
    trackFilterClusterUpdated, 
    trackFilterClusterDeleteClicked, 
    trackFilterClusterDeleted,
    trackBulkAssetEdited,
    trackBulkAssetsDeleted,
    trackContentMapExported,
    trackExportClicked
   } = useContentMapAdoption();

   const {
    trackAssetDeleted
   } = useStreamsAdoption();

  const activeClusterId = _.get(params, "id", "");

  const { loading: isCustomFieldsLoading } = useStoreState("customFields", true);
  const { loading: isClustersLoading, succeeded: isClustersFetched, items: clustersItems } = useStoreState("contentMap.clusters", true);
  const { loading: isClusterLoading, succeeded: isClusterFetched, item: cluster } = useStoreState(`contentMap.cluster.${activeClusterId}`, false);
  const { loading: isClusterCreating, succeeded: isClusterCreated, item: newCluster } = useStoreState("contentMap.creatingCluster", false);
  const { loading: isClusterDeleting, succeeded: isClusterDeleted } = useStoreState("contentMap.deletingCluster", false);
  const { loading: isClusterUpdating, succeeded: isClusterUpdated } = useStoreState("contentMap.updatingCluster", false);
  const { loading: isAssetsLoading, succeeded: isAssetsFetched, items: assets } = useStoreState("contentMap.assets.assetsList", true);
  const { succeeded: isAssetsInListUpdated } = useStoreState("contentMap.assetsList.update", false);
  const { loading: isAssetsDeleting, succeeded: isAssetsDeleted } = useStoreState("contentMap.assets.delete", false);
  const { loading: isAssetsUpdating, succeeded: isAssetsUpdated } = useStoreState("contentMap.assets.update", false);
  const { loading: isAssetsLoadingForDownload, succeeded: isAssetsFetchedForDownload,
    pagination: assetsPaginationForDownload, items: assetsForDownload, error: assetsFetchErrorForDownload } = useStoreState("contentMap.assets.download", true);
  

  const [filterData, setFilterData] = useState<Filter[]>();

  const [clusterName, setClusterName] = useState<string>("");
  const [deletingCluster, setDeletingCluster] = useState<boolean>(false);

  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [restrictedAssetsDeleteCount, setRestrictedAssetsDeleteCount] = useState<number>(0);
  const [assetsDeleting, setAssetsDeleting] = useState<boolean>(false);
  const [assetsUpdating, setAssetsUpdating] = useState<boolean>(false);
  const [assetsUpdateData, setAssetsUpdateData] = useState<UpdateAssetsRequest>();
  const [isAllAssetsSelected, setIsAllAssetsSelected] = useState<boolean>(false);
  const [isClusterDataChanged, setIsClusterDataChanged] = useState<boolean>(false);
  const [isClusterNameChanged, setIsClusterNameChanged] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = React.useState<ScoreCard | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>(selectedCard?.value || "all",);

  const createClusterProps: IUseFetchProps = {
    action: createCluster,
    loading: isClusterCreating,
    succeeded: isClusterCreated,
    response: newCluster,
    error: null
  };

  const { handleFetch: createClusterRequest } = useFetch(createClusterProps);

  const updateCollectionProps: IUseFetchProps = {
    action: updateCluster,
    loading: isClusterUpdating,
    succeeded: isClusterUpdated,
    response: cluster,
    error: null
  };

  const { handleFetch: updateClusterRequest } = useFetch(updateCollectionProps);

  const deleteClusterProps: IUseFetchProps = {
    action: deleteCluster,
    loading: isClusterDeleting,
    succeeded: isClusterDeleted,
    response: cluster,
    error: null
  };

  const { handleFetch: deleteClusterRequest } = useFetch(deleteClusterProps);

  const updateAssetsProps: IUseFetchProps = {
    action: updateAssets,
    loading: isAssetsUpdating,
    succeeded: isAssetsUpdated,
    response: [],
    error: null
  };

  const { handleFetch: updateAssetsRequest } = useFetch(updateAssetsProps);

  const deleteAssetsProps: IUseFetchProps = {
    action: deleteAssets,
    loading: isAssetsDeleting,
    succeeded: isAssetsDeleted,
    response: [],
    error: null
  };

  const { handleFetch: deleteAssetsRequest } = useFetch(deleteAssetsProps);

  useEffect(() => {
    const activeCluster = _.find(clustersItems, { id: activeClusterId });
    setClusterName(_.get(activeCluster, "name", ""));
  }, [activeClusterId, isClustersFetched, isClusterUpdated])

  useEffect(() => {

    const clusterFilters = _.get(cluster, "filters", []);
    if(_.size(clusterFilters) > 0) {
      trackFilterClusterSelected({id: activeClusterId})
    }

    const filterDataExcludingId = _.map(filterData, (filter) => {
      return _.omit(filter, "id");
    })

    const transformedFilter = _.map(clusterFilters, (filter) => {
      if ([PropertyEnum.asset, PropertyEnum.stream, PropertyEnum.group].includes(filter.property.type)) {
        const extractedFilter = {
          ...filter,
          value: _.map(filter.value, "id")
        }
        return extractedFilter;
      }
      return filter;
    })

    setIsClusterDataChanged(!_.isEqual(transformedFilter, filterDataExcludingId))

  }, [isClusterFetched, filterData, activeClusterId, isClusterUpdated])

  useEffect(() => {
    if (activeClusterId !== "all" && isClusterFetched) {
      setIsClusterNameChanged(!_.isEqual(cluster.name, clusterName))
    }
  }, [clusterName])



  useEffect(() => {
    trackContentMapScreenView();
    dispatch(fetchContentScore());
    if (!isClustersLoading && !isClustersFetched) {
      dispatch(fetchClusters());
    }

    if (!isCustomFieldsLoading) {
      dispatch(fetchCustomFields(true));
    }

    return () => {
      dispatch(invalidateContentData());
    }
  }, [])

  useEffect(() => {
    scrollToCluster(activeClusterId)
  }, [_.size(clustersItems)])

  useEffect(() => {
    if (activeClusterId !== "all" && !isClusterFetched) {
      dispatch(fetchCluster({ id: activeClusterId }));
    }
    dispatch(invalidateContentMapAssets());
    setSelectedAssets([]);
    setRestrictedAssetsDeleteCount(0);
  }, [activeClusterId])

  useEffect(() => {
    if (isAllAssetsSelected) {
      setSelectedAssets(assets)
    }
  }, [isAssetsLoading, isAssetsFetched])

  useEffect(() => {

    if (_.size(assets) > 0 && _.size(selectedAssets) === _.size(assets)) {
      setIsAllAssetsSelected(true)
    }
    else {
      setIsAllAssetsSelected(false);
    }

  }, [selectedAssets])

  useEffect(() => {
    if (!isClusterCreating && isClusterCreated) {
      dispatch(fetchClusters());
      navigate(`/hub/content-map/${newCluster.id}`);
      toast(<Toast.Sucess message={`Folder ${newCluster.name} created successfully`} />, {
        toastId: newCluster.id,
      });
    }
  }, [isClusterCreating, isClusterCreated])

  useEffect(() => {
    if (!isClusterUpdating && isClusterUpdated) {
      toast(<Toast.Sucess message={`Folder ${cluster.name} updated successfully`} />, {
        toastId: activeClusterId,
      });
    }
  }, [isClusterUpdating, isClusterUpdated])

  useEffect(() => {
    if (!isClusterDeleting && isClusterDeleted) {
      setDeletingCluster(false);
      dispatch(fetchClusters());
      navigate("/hub/content-map/all");
      scrollLeft();
      toast(<Toast.Sucess message={`Deleted successfully`} />, {
        toastId: activeClusterId,
      });
    }
  }, [isClusterDeleting, isClusterDeleted])

  useEffect(() => {
    if (!isAssetsDeleting && isAssetsDeleted) {
      setAssetsDeleting(false);
      setSelectedAssets([]);
      handleFetchAssets(0);
      toast(<Toast.Sucess message={`${_.size(selectedAssets) === 1 ? "Asset" : "Assets"} deleted successfully`} />, {
        toastId: activeClusterId,
      });
    }
  }, [isAssetsDeleting, isAssetsDeleted])

  useEffect(() => {
    if (!isAssetsUpdating && isAssetsUpdated) {
      setAssetsUpdating(false);
      setSelectedAssets([]);
      handleFetchAssets(0);
      toast(<Toast.Sucess message={`Assets updated successfully`} />, {
        toastId: activeClusterId,
      });
    }
  }, [isAssetsUpdating, isAssetsUpdated, isAssetsInListUpdated])

  useEffect(() => {
    // invalidate selected assets when a new card or filter is selected
    setSelectedAssets([]);
  }, [selectedCard, selectedFilter])

  const scrollLeft = () => {
    const scrollContainer = document.getElementById("scroll-container");
    if (scrollContainer) {
      scrollContainer.scrollBy({
        left: -_.get(scrollContainer, "scrollLeft", 0),
        behavior: 'smooth'
      })
    }
  }

  const scrollToCluster = (id: string) => {

    const clusterElement = document.getElementById(id);
    if (clusterElement) {
      clusterElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }

  }

  const handleBackClick = () => {
    navigate("/hub");
  }

  const handleFilterUpdate = (filters: Filter[], shouldFetchAssets: boolean) => {

    const request = {
      requestBody: {
        filters: filters,
        orderBy: "ASC",
        query: "",
        sortBy: ""
      },
      page: 0,
      isForDownload: false
    }

    setFilterData(filters);

    if (!isAssetsLoading && shouldFetchAssets) {
      dispatch(fetchAssets(request));
    }

  }

  const handleClusterAction = (type: string) => {

    if (_.size(clusterName) === 0) {
      toast(<Toast.Failure message={`Please give a folder name`} />, {
        toastId: activeClusterId,
      });
      return;
    }

    if (type === "save") {
      const requestBody = {
        filters: filterData ? filterData : [],
        filtersSet: true,
        name: clusterName ? clusterName : "untitled",
        nameSet: true
      }

      createClusterRequest({ requestBody }).then((response) => {
        trackFilterClusterCreated({response});
      });
    }
    else if (type === "update") {
      const requestBody = {
        filters: filterData ? filterData : [],
        filtersSet: true,
        name: clusterName ? clusterName : "untitled",
        nameSet: true
      }

      updateClusterRequest({ requestBody, id: activeClusterId }).then((response) => {
        trackFilterClusterUpdated({response});
      });
    }
  }

  const handleClusterNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isClusterLoading) {
      setClusterName(e.target.value);
    }
  }

  const handleDeleteCluster = () => {
    if (!isClusterDeleting) {
      deleteClusterRequest({ id: activeClusterId }).then((response) => {
        trackFilterClusterDeleted({response});
      });
    }
  }

  const handleFetchAssets = (page: number, isForDownload: boolean = false) => {

    const request = {
      requestBody: {
        filters: filterData ? filterData : [],
        orderBy: "ASC",
        query: "",
        sortBy: ""
      },
      page: page,
      isForDownload: isForDownload
    }

    if (selectedCard) {
      dispatch(fetchContentGraphValues({
        category: selectedCard.key,
        value: selectedFilter,
      }));
      dispatch(
        fetchAssetsForContentScoreCard({ page: page, category: selectedCard.key, value: selectedFilter })
      );
      dispatch(fetchContentScore());
    }

    if(!selectedCard){
      if (isForDownload) {
        dispatch(fetchAssetsForDownload(request));
      } else {
        dispatch(fetchAssets(request));
      }
    }
  }

  const handleContentActions = (action: AssetsActionEnum) => {
    if (action === AssetsActionEnum.delete_assets) {

      const children = [] as Asset[];
      selectedAssets.map((child) => {
        if (_.get(child, "source", "") === null || _.get(child, "canDelete", false)) {
          children.push(child);
        }
      })

      const restrictedAssetsCount = _.size(selectedAssets) - _.size(children)
      setSelectedAssets(children);
      setRestrictedAssetsDeleteCount(restrictedAssetsCount);
      setAssetsDeleting(true);

    }
    else if (action === AssetsActionEnum.update_assets) {
      setAssetsUpdating(true);
    }
  }

  const handleDeleteAssets = () => {
    const assetIds = _.map(selectedAssets, 'id');

    const requestBody = {
      filters: [
        {
          operator: Operators.is,
          property: { type: PropertyEnum.asset },
          value: assetIds
        }
      ]
    }

    deleteAssetsRequest(requestBody).then(() => {
      if(_.size(assetIds) > 1){
        trackBulkAssetsDeleted({sectionType: getSectionType(selectedCard?.key || "")});
      } else {
        trackAssetDeleted({response: selectedAssets, sourceCta: CONTENT_MAP, sectionType: getSectionType(selectedCard?.key || "")});
      }
    });
  }

  const transitions = useTransition(activeClusterId !== "all", {
    from: { opacity: 0, transform: 'translateX(-20px)' }, // Start off to the left
    enter: { opacity: 1, transform: 'translateX(0)' },   // Slide in and fade in
    config: { tension: 100, friction: 10 },
  });

  const handleAssetsUpdate = () => {
    const requestBody = {
      ...assetsUpdateData,
      filters: [
        {
          operator: Operators.is,
          property: { type: PropertyEnum.asset },
          value: _.map(selectedAssets, 'id')
        }
      ]
    }
    updateAssetsRequest(requestBody as UpdateAssetsRequest).then(() => {
      trackBulkAssetEdited({sectionType: getSectionType(selectedCard?.key || "")});
    });
  }


  const showFooter = React.useMemo(() => {
    if (activeClusterId === "all" && _.size(filterData) === 1) {
      const filter = filterData?.[0];
      if (filter && filter.operator === Operators.is && filter.property.type === PropertyEnum.asset && _.size(filter.value) === 0) {
        return false;
      }
      return true
    }
    else if ((activeClusterId !== "all" && !isClusterFetched) || (isClusterFetched && !isClusterDataChanged)) {
      return false;
    }
    return true;
  }, [filterData, isClusterLoading, activeClusterId, isClusterFetched, isClusterDataChanged])

  const handleClose = () => {
    trackContentMapExported();
  }


  const showUpdate = activeClusterId !== "all" && !isClusterLoading;
  const disableNavigation = isClusterCreating || isClusterUpdating || isClusterDeleting || isAssetsDeleting || isAssetsLoading || isClusterLoading || isAssetsLoadingForDownload;
  const fetchState = {
    loading: isAssetsLoadingForDownload,
    succeeded: isAssetsFetchedForDownload,
    error: assetsFetchErrorForDownload
  }

  const footerAnimation =
    useSpring({
      height: showFooter || isClusterNameChanged ? "35px" : "0px",
      opacity: showFooter || isClusterNameChanged ? 1 : 0,
      overflow: "hidden",
      config: { tension: 220, friction: 20 },
    });

  const folderNameAnimation =
    useSpring({
      height: activeClusterId === "all" ? "30px" : "0px",
      opacity: activeClusterId === "all" ? 1 : 0,
      overflow: "hidden",
      config: { tension: 220, friction: 20 },
    });
    

  return (
    <div className={styles.content}>
      <div className={styles.outerContainer}>
        <div onClick={handleBackClick} className={styles.backContainer}>
          <i className="paperflite-left-arrow"></i>
          <span className={styles.title}>content hub </span>
        </div>

        {_.size(assets) > 0 && !selectedCard && (
          <div className={styles.downloadButton}>
            <DownloadExcel
              title="Asset Master Report"
              fileName="AssetMasterReport"
              schema={MASTER_ASSET_SCHEMA}
              data={formatDataForDownload(assetsForDownload)}
              fetchState={fetchState}
              pagination={assetsPaginationForDownload}
              fetchDataForPage={(page) => handleFetchAssets(page, true)}
              invalidateData={() => dispatch(invalidateDownloadAssets())}
              handleClose={handleClose}
              downloadClick={() => trackExportClicked()}
            />
          </div>
        )}

        <div className={styles.headerContainer}>
          <div className={styles.header}> content map </div>
          <div className={styles.subText}>
            {" "}
            slice, dice, stash & cluster – Your content command center{" "}
          </div>
        </div>

        <ContentScore 
        selectedCard={selectedCard} 
        selectedFilter={selectedFilter}
        setSelectedCard={setSelectedCard} 
        setSelectedFilter={setSelectedFilter}
        />

        {!selectedCard && (
          <div className={styles.clustersContainer}>
          {transitions((customStyles, item) =>
            item ? (
              <animated.div style={{ ...customStyles }}>
                <NavLink
                  to={disableNavigation ? "" : "/hub/content-map/all"}
                  onClick={scrollLeft}
                  className={styles.addListButton}
                >
                  <i className={cx(styles.icon, "paperflite-add")}></i>
                  <div className={cx(styles.name, styles.add)}>New Folder</div>
                </NavLink>
              </animated.div>
            ) : null,
          )}

          <span
            style={{ maxWidth: activeClusterId === "all" ? "90vw" : "87vw" }}
          >
            <ScrollableContainer distance={300}>
              <NavLink
                id={"cluster-all"}
                to={disableNavigation ? "" : "/hub/content-map/all"}
                className={cx(styles.cluster, styles.all, {
                  [styles.active]: activeClusterId === "all",
                })}
              >
                <div className={cx(styles.name)}>All Contents</div>
              </NavLink>

              {_.size(clustersItems) > 0 &&
                clustersItems.map((cluster: Cluster) => {
                  if (activeClusterId === cluster.id) {
                    return (
                      <span
                        key={cluster.id}
                        id={cluster.id}
                        className={cx(styles.cluster, {
                          [styles.active]: activeClusterId === cluster.id,
                        })}
                      >
                        <AutoGrowInput
                          className={cx(styles.input)}
                          value={clusterName}
                          onChange={handleClusterNameChange}
                        />
                        <i
                          onClick={() => {
                            trackFilterClusterDeleteClicked({
                              filterClusterName: cluster.name,
                            });
                            setDeletingCluster(true);
                          }}
                          className="paperflite-close"
                        />
                      </span>
                    );
                  }

                  return (
                    <NavLink
                      id={cluster.id}
                      to={
                        disableNavigation
                          ? ""
                          : `/hub/content-map/${cluster.id}`
                      }
                      key={cluster.id}
                      className={cx(styles.cluster, {
                        [styles.active]: activeClusterId === cluster.id,
                      })}
                    >
                      <div className={cx(styles.name)}>{cluster.name}</div>
                    </NavLink>
                  );
                })}
            </ScrollableContainer>
          </span>
        </div>
        )}

      </div>
      <div className={styles.innerContainer}>
        {!selectedCard && (
          <div
          className={cx(styles.clusterContainer, {
            [styles.clusterLoading]: isClusterLoading,
          })}
        >
          <animated.div style={folderNameAnimation}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                value={clusterName}
                placeholder="Add a folder name..."
                onChange={handleClusterNameChange}
              />
            </div>
          </animated.div>

          {!isClusterLoading && (
            <>
              <ClusterView
                cluster={cluster}
                handleFilterUpdate={handleFilterUpdate}
              />

              <animated.div style={footerAnimation}>
                <div className={styles.footer}>
                  {showUpdate && (
                    <span className={styles.updateFolder}>
                      <IconButton
                        icon="paperflite-reload"
                        disabled={isClusterCreating || isClusterUpdating}
                        title="Update"
                        onClick={() => handleClusterAction("update")}
                      />
                      <span className={styles.divider}></span>
                    </span>
                  )}

                  <IconButton
                    className={styles.createFolder}
                    icon="paperflite-tick"
                    title="Create folder"
                    disabled={isClusterCreating || isClusterUpdating}
                    onClick={() => handleClusterAction("save")}
                  />
                </div>
              </animated.div>
            </>
          )}
        </div>
        )}

        <AssetList
          handleContentActions={handleContentActions}
          selectedAssets={selectedAssets}
          setSelectedAssets={setSelectedAssets}
          handleFetchAssets={handleFetchAssets}
          selectedCard={selectedCard}
        />

      </div>

      <Slider.Dialog
        width="300"
        show={deletingCluster}
        onClose={() => setDeletingCluster(false)}
      >
        <Slider.Header title="Deleting" />

        <Slider.Body>
          <p>Are you sure you want to delete this folder &quot; {clusterName} &quot; ?</p>
        </Slider.Body>

        <Slider.Actions>
          <Button
            icon="paperflite-tick"
            text={"Confirm"}
            variant="primary"
            disabled={isClusterDeleting}
            onClick={() => handleDeleteCluster()}
          />
          <Button
            text="Cancel"
            variant="secondary"
            disabled={isClusterDeleting}
            onClick={() => setDeletingCluster(false)}
          />
        </Slider.Actions>
      </Slider.Dialog>

      <Slider.Dialog
        width="300"
        show={assetsDeleting}
        onClose={() => setAssetsDeleting(false)}
      >
        <Slider.Header
          title="deleting..."
          message={`${_.size(selectedAssets)} Assets`}
        >
          <p>Are you sure you would like to delete the following assets?</p>
        </Slider.Header>

        <Slider.Body>
          <div className={styles.deleteAssetsContainer}>
            {selectedAssets.map((asset) => (
              <div key={asset.id} className={styles.asset}>
                <Thumbnail
                  media={asset.icon}
                  width={260}
                  height={205}
                  className={styles.thumbnail}
                  alt="asset thumbnail"
                  defaultImage={defaultAssetImg}
                />
                <div className={styles.content}>
                  <span>
                    <Text text={asset.name} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Slider.Body>

        <Slider.Actions>
          {restrictedAssetsDeleteCount > 0 && (
            <div className={styles.confirmationMessage}>
              <WarningMessage
                type="WARNING"
                header="However"
                message={`You don't have permission to delete ${restrictedAssetsDeleteCount} of the selected assets.`}
              />
            </div>
          )}
          <Button
            icon="paperflite-tick"
            text={"Confirm"}
            variant="primary"
            disabled={isAssetsDeleting || _.size(selectedAssets) === 0}
            onClick={() => handleDeleteAssets()}
          />
          <Button
            text="Cancel"
            variant="secondary"
            disabled={isAssetsDeleting}
            onClick={() => {
              setAssetsDeleting(false);
              setRestrictedAssetsDeleteCount(0);
              setSelectedAssets([]);
            }}
          />
        </Slider.Actions>
      </Slider.Dialog>

      <Slider.Dialog
        className={styles.updateAssetsSlider}
        width="300"
        show={assetsUpdating}
        onClose={() => setAssetsUpdating(false)}
      >
        <Slider.Header
          className={styles.header}
          title="bulk edit"
          message="Add/Edit all selected assets. Bulk processing: because ain't nobody got time for individual edits."
        />

        <Slider.Body>
          <UpdateAssets setAssetsUpdateData={setAssetsUpdateData} />
        </Slider.Body>

        <Slider.Actions>
          <Button
            icon="paperflite-tick"
            text={isAssetsUpdating ? "Confirming..." : "Confirm"}
            variant="primary"
            disabled={isAssetsUpdating}
            onClick={() => handleAssetsUpdate()}
          />
          <Button
            text="Cancel"
            variant="secondary"
            disabled={isAssetsUpdating}
            onClick={() => {
              setAssetsUpdating(false);
            }}
          />
        </Slider.Actions>
      </Slider.Dialog>
    </div>
  );
}

const IconButton: React.FC<any> = ({ icon, title, onClick, disabled, className }) => {
  return (
    <button disabled={disabled} onClick={onClick} className={cx(styles.iconButton, {
      [styles.disabled]: disabled
    }, className)}>
      <i className={icon}></i>
      <span className={styles.title}>{title}</span>
    </button>
  )
}

const UpdateAssets: React.FC<any> = ({ setAssetsUpdateData }) => {

  const { items: customFields } = useStoreState("customFields", true);
  const account = useSelector((state: any) => _.get(state, "accounts.item", null));

  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedFilter, setSelectedFilter] = useState<{ value: string, expiryDate: string }>();
  const [fields, setFields] = useState<CustomFieldItem[]>([])
  const [tags, setTags] = useState<string[]>([]);
  const [shareAccess, setShareAccess] = useState<AssetAccess>(AssetAccess.none);
  const [downloadAccess, setDownloadAccess] = useState<AssetAccess>(AssetAccess.none);

  useEffect(() => {

    let expriyDate;
    if (selectedFilter?.expiryDate) {
      const momentValue = moment(selectedFilter.expiryDate, 'DD MMMM YYYY');
      expriyDate = momentValue.format('YYYY-MM-DD');
    }

    setAssetsUpdateData({
      expiryDate: expriyDate ? expriyDate : null,
      customFields: fields,
      tags,
      permissions: {
        download: downloadAccess,
        share: shareAccess
      }
    })

  }, [selectedDate, selectedFilter, fields, tags, shareAccess, downloadAccess])

  const handleFilterClick = (item) => {
    const expiryDate = getFilterDate(item.value);
    const selectedDate = moment(expiryDate).format('DD MMM YYYY');
    setSelectedFilter({ value: item.value, expiryDate: selectedDate });
    setSelectedDate(selectedDate);
  }

  const getFilterDate = (value: string) => {
    const currentDate = moment();
    switch (value) {
      case "one_month":
        return currentDate.add(1, 'months');
      case "three_month":
        return currentDate.add(3, 'months');
      case "six_month":
        return currentDate.add(6, 'months');
      case "twelve_month":
        return currentDate.add(12, 'months');
    }

  }

  const handleDateChange = (date: Date) => {
    const parsedDate = new Date(date);
    const formattedDate = moment(parsedDate).format('DD MMM YYYY'); // formatted to display in ui
    setSelectedDate(formattedDate);
    setSelectedFilter({ value: "custom", expiryDate: formattedDate });
    document.body.click();
  }

  const handleCustomFieldSelect = (newFields: CustomFieldItem[]) => {
    const updatedFields = [...fields];
  
    newFields.forEach((newField) => {
      const fieldIndex = updatedFields.findIndex((field) => field.id === newField.id);
  
      if (fieldIndex >= 0) {
        updatedFields[fieldIndex] = { 
          ...updatedFields[fieldIndex], 
          value: newField.value 
        };
      } else {
        updatedFields.push(newField);
      }
    });
  
    setFields(updatedFields);
  };

  const handleTagValuesUpdate = (tags) => {
    setTags(tags);
  }

  const expireContent = _.get(account, 'contentSettings.expireContent', false);
  
  return (
    <div className={styles.updateAssetsContainer}>
      <div className={styles.expiration}>
        <span>Expiration </span>
        <Dropdown>

          <Dropdown.Toggle as="div" className={styles.dropDown}>
            <i className='paperflite-edit' />
            <div className={styles.selectedDate}>{selectedDate ? selectedDate : "Pick a date"}</div>
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {UPDATE_CONTENT_EXPIRY_FILTERS.map((item) => {
              const isActive = selectedFilter?.value === item.value;
              return (
                <Dropdown.Item
                  key={item.value}
                  onClick={() => handleFilterClick(item)}
                >
                  <i
                    className={cx(styles.selectIcon, {
                      [styles.activeSelect]: isActive
                    }, isActive ? "fa-solid fa-check-circle" : 'paperflite-radio-button-unselected'
                    )} />
                  <span className={styles.name}>{item.name}</span>

                </Dropdown.Item>
              )
            })}
            <div className={styles.datePickerComponent}>
              <DatePickerComponent className={styles.calendarOuterContainer} value={selectedFilter?.expiryDate} handleDateChange={handleDateChange} />
            </div>

          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className={styles.accessContainer}>
        <div>
          <p>Share Access</p>
          <span onClick={() => setShareAccess(AssetAccess.true)}><i className={cx(
            { [styles.active]: shareAccess === "TRUE" },
            shareAccess === "TRUE" ? "paperflite-radio-button-selected" : 'paperflite-radio-button-unselected'
          )} /> Allow </span>

          <span onClick={() => setShareAccess(AssetAccess.false)}><i className={cx(
            { [styles.active]: shareAccess === "FALSE" },
            shareAccess === "FALSE" ? "paperflite-radio-button-selected" : 'paperflite-radio-button-unselected'
          )} /> Restrict </span>

          <span onClick={() => setShareAccess(AssetAccess.none)}><i className={cx(
            { [styles.active]: shareAccess === "NONE" },
            shareAccess === "NONE" ? "paperflite-radio-button-selected" : 'paperflite-radio-button-unselected'
          )} /> No update </span>
        </div>

        <div>
          <p>Download Access</p>
          <span onClick={() => setDownloadAccess(AssetAccess.true)}><i className={cx(
            { [styles.active]: downloadAccess === "TRUE" },
            downloadAccess === "TRUE" ? "paperflite-radio-button-selected" : 'paperflite-radio-button-unselected'
          )} /> Allow </span>

          <span onClick={() => setDownloadAccess(AssetAccess.false)}><i className={cx(
            { [styles.active]: downloadAccess === "FALSE" },
            downloadAccess === "FALSE" ? "paperflite-radio-button-selected" : 'paperflite-radio-button-unselected'
          )} /> Restrict </span>

          <span onClick={() => setDownloadAccess(AssetAccess.none)}><i className={cx(
            { [styles.active]: downloadAccess === "NONE" },
            downloadAccess === "NONE" ? "paperflite-radio-button-selected" : 'paperflite-radio-button-unselected'
          )} /> No update </span>
        </div>
        {expireContent && <p className={styles.note}>Note: Share and download access updates will not apply to expired assets. Please extend the expiry date in order to make these changes.</p>}
      </div>

      <div className={styles.tagsContainer}>
        Tags
        <TagsInput className={styles.tagInput} selectedTags={tags} handleTagValuesUpdate={handleTagValuesUpdate} />

      </div>
      <div className={styles.customFieldsContainer}>


        {_.size(customFields) > 0 &&
          customFields.map((field: any) => {
            return (
              <EditCustomField
                key={field.id}
                field={field}
                selectedCustomFields={fields}
                onOptionChange={handleCustomFieldSelect}
              />
            );
          })}
      </div>

    </div>
  )
}


const formatDataForDownload = (data) => {

  if (_.isEmpty(data)) {
    return [];
  }

  const items: any[] = [];

  data.forEach((value) => {

    const fileType = FileService.getFileType(value.metadata);

    const audioDuration = value.metadata.audio_duration;

    const item = {};
    item["id"] = value.id;
    item["name"] = value.name;
    item["extension"] = FileService.getFileType(value.metadata);

    if (_.get(value, "metadata.content_length") > 0) {
      const fileSize = FileService.formatSize(value.metadata.content_length);
      item["filesize"] = fileSize;
    }

    const fileFormats = ["PDF", "DOCX", "DOC", "PPT", "PPTX", "G-DOC", "PPSX", "G-SLIDES"];
    const pageCount = value.metadata.pages;

    if (_.includes(fileFormats, fileType)) {
      if (pageCount === 0 || pageCount === 1) {
        item["assetmetadata"] = `${pageCount} Page`;
      } if (pageCount !== undefined || null) {
        item["assetmetadata"] = `${pageCount} Pages`;
      }
    }

    const mediaFormats = ["VIDEO", "YOUTUBE", "WISTIA", "VIMEO", "LINK", "VIDYARD"];

    if (_.includes(mediaFormats, fileType)) {


      if (_.get(value, "metadata.video_duration")) {

        const videoDuration = FileService.formatDuration(_.get(value, "metadata.video_duration"));

        item["assetmetadata"] = videoDuration;
      }
      else {
        item["assetmetadata"] = "";
      }

    }

    const otherFileFormats = ["HTML APP", "SCORM", "LINK", "ZIP", "FILE"]

    if (_.includes(otherFileFormats, fileType)) {

      item["assetmetadata"] = "";

    }


    item["url"] = `${window.location.protocol}//${window.location.host}/assets/${value.id}`;

    if (fileType === "AUDIO") {

      if (audioDuration === undefined) {

        item["assetmetadata"] = ""
      }
      else if (audioDuration !== 0) {

        const minutes = Math.floor(audioDuration / 60);
        const seconds = Math.round(audioDuration % 60);

        let timeSpentValue = `${FileService.padNumber(minutes, 1)} Minute${minutes === 1 ? "" : "s"}`;

        if (seconds > 0) {
          timeSpentValue += ` ${FileService.padNumber(seconds, 1)} Second${seconds === 1 ? "" : "s"}`;
        }

        item["assetmetadata"] = timeSpentValue;

      }
    }


    item["source"] = value.source !== null ? value.source : "Paperflite Cloud";



    if (value.summary !== null) {

      const internalDescription = `${value.summary}`;
      const div = document.createElement("div");
      div.innerHTML = internalDescription;
      const text = div.textContent || div.innerText || "";
      item["summary"] = text;

    }


    if (value.externalSummary !== null) {

      const externalDescription = `${value.externalSummary}`;
      const div = document.createElement("div");
      div.innerHTML = externalDescription;
      const text = div.textContent || div.innerText || "";
      item["externalSummary"] = text;

    }


    if (value.tags !== null) {
      item["tags"] = value.tags.join("|");
    }

    if (_.get(value, "author.email", null) !== null) {
      item["author"] = value.author.email;
    }


    if (value.customFields !== null) {
      const customFieldOutput = value.customFields.map((item) => {
        const name = item.name;
        const finalArray = item.value.map((itemValue) => itemValue.value);

        return `${name} [ ${finalArray.join('|')}]`;
      });

      const result = customFieldOutput.join(' , ');

      item["customFields"] = result;
    }


    if (value.createdDate) {
      item["createdDate"] = moment(value.createdDate).format("YYYY-MMM-DD");
    }

    if (value.modifiedDate) {
      item["updatedDate"] = moment(value.modifiedDate).format("YYYY-MMM-DD");
    }

    if (value.expiryDate) {
      item["expiryDate"] = moment(value.expiryDate).format("YYYY-MMM-DD");
    }

    item["internalDownloads"] = value.internalDownloads;
    item["externalDownloads"] = value.externalDownloads;
    item["avgExternalTimeSpent"] = _.round(value.avgExternalTimeSpent != null ? value.avgExternalTimeSpent : 0, 1);
    item["avgInternalTimeSpent"] = _.round(value.avgInternalTimeSpent != null ? value.avgInternalTimeSpent : 0, 1);
    item["internalViews"] = value.internalViews;
    item["externalViews"] = value.externalViews;
    item["avgTimeSpent"] = _.round(value.avgTimeSpent != null ? value.avgTimeSpent : 0, 1);
    item["streamNames"] = Array.isArray(value.streamNames) ? value.streamNames.join(', ') : '';
    item["groupNames"] = Array.isArray(value.groupNames) ? value.groupNames.join(', ') : '';
    item["shares"] = value.shares;

    items.push(item);

  });

  return items;

}


export default ContentMap;